// Standard library imports
import React, { useContext, useEffect, useState } from 'react';

// External library imports
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { UilTrash, UilPen } from '@iconscout/react-unicons';
import _, { initial } from 'lodash';

// Internal module imports
import Typography from '../../../../components/Typography/Typography';
import Table from '../../../../components/Table/Table';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import { DropdownComponent, Input, ToggleButton } from '../../../../components/Inputs/Input';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import { useLoader } from '../../../../hooks/use-loader.hook';
import CustomTooltip from '../../../../components/CustomToolTip/CustomTooltip';
import MultiSelectDropdown from '../../../../components/Inputs/MultiSelectDropdown';

// Css imports
import classes from '../../../../styles/AllDevices.module.css';
import GlobalConfigClasses from '../index.module.css';
import { BidCredentialCreate } from '../../../../validations/Root/BidCredentialValidation';
import { regulationsService } from '../../../../services/regulationsService';
import { BidCredentialsService } from '../../../../services/BidCredentialsService';
import { ConfigContext } from '../../../../context/ConfigContext';

const getInitialValues = (data = {}, markets = [{ label: 'FCR', value: 'FCR' }], marketGroup, selectedMarketGroup) => {
    const dataMarkets = data?.market?.length ? data?.market.map((item) => ({ label: item, value: item })) : '';
    if (markets.some((market) => marketGroup.FCR.includes(market.value))) {
        return {
            name: data?.name || '',
            market: dataMarkets || markets,
            email: data?.email || '',
            BASE_URL: data?.BASE_URL || '',
            USERNAME: data?.USERNAME || '',
            PASSWORD: data?.PASSWORD || '',
        };
    } else if (markets.some((market) => marketGroup.FCR_D.includes(market.value)) && selectedMarketGroup?.value === 'FCR_D') {
        return {
            name: data?.name || '',
            market: dataMarkets || markets,
            email: data?.email || '',
            BID_RESULT_BASE_URL: data?.BID_RESULT_BASE_URL || '',
            AUTH_TOKEN: data?.AUTH_TOKEN || '',
        };
    } else if (markets.some((market) => marketGroup.FFR.includes(market.value))) {
        return {
            name: data?.name || '',
            market: dataMarkets || markets,
            email: data?.email || '',
            BID_RESULT_BASE_URL: data?.BID_RESULT_BASE_URL || '',
            AUTH_TOKEN: data?.AUTH_TOKEN || '',
        };
    } else if (markets.some((market) => marketGroup.AFRR.includes(market.value))) {
        return {
            name: data?.name || '',
            market: dataMarkets || markets,
            email: data?.email || '',
            BASE_URL: data?.BASE_URL || '',
            API_KEY: data?.API_KEY || '',
            BA_MESSAGE_ID: data?.BA_MESSAGE_ID || '',
            SELF_EIC: data?.SELF_EIC || '',
            ENERGY_EIC: data?.ENERGY_EIC || '',
            CAPACITY_EIC: data?.CAPACITY_EIC || '',
            ENERGY_ADDRESS: data?.ENERGY_ADDRESS || '',
            CAPACITY_ADDRESS: data?.CAPACITY_ADDRESS || '',
            AVAILABILITY_PLANS_ADDRESS: data?.AVAILABILITY_PLANS_ADDRESS || '',
            RESOURCE_SCHEDULE_ADDRESS: data?.RESOURCE_SCHEDULE_ADDRESS || '',
        };
    } else if (markets.some((market) => marketGroup.MFRR.includes(market.value))) {
        return {
            name: data?.name || '',
            market: dataMarkets || markets,
            email: data?.email || '',
            BASE_URL: data?.BASE_URL || '',
            API_KEY: data?.API_KEY || '',
            BA_MESSAGE_ID: data?.BA_MESSAGE_ID || '',
            SELF_EIC: data?.SELF_EIC || '',
            ENERGY_EIC: data?.ENERGY_EIC || '',
            CAPACITY_EIC: data?.CAPACITY_EIC || '',
            ENERGY_ADDRESS: data?.ENERGY_ADDRESS || '',
            CAPACITY_ADDRESS: data?.CAPACITY_ADDRESS || '',
            AVAILABILITY_PLANS_ADDRESS: data?.AVAILABILITY_PLANS_ADDRESS || '',
            RESOURCE_SCHEDULE_ADDRESS: data?.RESOURCE_SCHEDULE_ADDRESS || '',
        };
    } else if (markets.some((market) => marketGroup.DC_PLANNER.includes(market.value))) {
        return {
            name: data?.name || '',
            market: dataMarkets || markets,
            email: data?.email || '',
            BASE_URL: data?.BASE_URL || '',
            USERNAME: data?.USERNAME || '',
            PASSWORD: data?.PASSWORD || '',
        };
    } else if (markets.some((market) => marketGroup.SPOT_REG.includes(market.value))) {
        return {
            name: data?.name || '',
            market: dataMarkets || markets,
            email: data?.email || '',
        };
    } else if (markets.some((market) => marketGroup.EA.includes(market.value))) {
        return {
            name: data?.name || '',
            market: dataMarkets || markets,
            email: data?.email || '',
        };
    } else if (markets.some((market) => marketGroup.FCR_D_FT.includes(market.value))) {
        return {
            name: data?.name || '',
            market: dataMarkets || markets,
            email: data?.email || '',
            FORTUM_BASE_URL: data?.FORTUM_BASE_URL || '',
            KEY: data?.KEY || '',
            SECRET: data?.SECRET || '',
        };
    } else if (markets.some((market) => marketGroup.FCR_D_N_NMMS.includes(market.value))) {
        return {
            name: data?.name || '',
            market: dataMarkets || markets,
            email: data?.email || '',
            BASE_URL: data?.BASE_URL || '',
            API_KEY: data?.API_KEY || '',
            BA_MESSAGE_ID: data?.BA_MESSAGE_ID || '',
            SELF_EIC: data?.SELF_EIC || '',
            CAPACITY_EIC: data?.CAPACITY_EIC || '',
            CAPACITY_ADDRESS: data?.CAPACITY_ADDRESS || '',
        };
    }
};

const BidCredentials = () => {
    const {
        state: { marketGroup },
    } = useContext(ConfigContext);
    const [startLoader, stopLoader] = useLoader();
    const [tableData, setTableData] = useState([]);
    const [addModal, setAddModal] = useState({
        status: false,
        data: {},
    });
    const [deleteModal, setDeleteModal] = useState({ status: false, id: null });
    // const [formConfig, setFormConfig] = useState({});
    const [marketGroupOptions, setMarketGroupOptions] = useState([]);
    const [selectedMarketGroup, setSelectedMarketGroup] = useState({});
    const [marketOptions, setMarketOptions] = useState([]);
    const [selectedMarket, setSelectedMarket] = useState([{ label: 'FCR', value: 'FCR' }]);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const processedData = [];
        let marketGroupFound = {};

        if (Object.keys(marketGroup).length && addModal.status) {
            for (const marketType in marketGroup) {
                const markets = marketGroup[marketType];
                if (addModal.data?._id && addModal.data?.market && markets.some((market) => addModal.data?.market.includes(market))) {
                    marketGroupFound = {
                        label: marketType,
                        value: marketType,
                        markets: markets.map((item) => ({ label: item, value: item })),
                    };
                }
                processedData.push({
                    label: marketType,
                    value: marketType,
                    markets: markets.map((item) => ({ label: item, value: item })),
                });
            }
        }
        setMarketGroupOptions(processedData);

        if (addModal.data?._id && addModal.data?.market && processedData.length) {
            setSelectedMarketGroup(marketGroupFound);
            setMarketOptions(marketGroupFound.markets);
            setSelectedMarket(addModal.data?.market.map((item) => ({ label: item, value: item })));
        } else if (processedData.length) {
            setSelectedMarketGroup(processedData[0]);
            setMarketOptions(processedData[0].markets);
            setSelectedMarket([processedData[0].markets[0]]);
        } else {
            setSelectedMarketGroup({});
            setMarketOptions([]);
        }
    }, [marketGroup, addModal.status]);

    const fetchData = () => {
        BidCredentialsService.ReadAll(
            () => startLoader('getCreds'),
            handleSuccess,
            handleError,
            () => stopLoader('getCreds')
        );
    };

    const handleSuccess = ({ data }) => {
        const processedData = data?.data.map((key, i) => ({
            's.no': i + 1,
            name: key.name,
            market:
                key.market?.length > 1 ? (
                    <CustomTooltip content={key.market.join(',')}>{key.market.join(',').substring(0, 35) + '...'}</CustomTooltip>
                ) : (
                    key.market.join(',') || '--'
                ),
            action: (
                <div className={GlobalConfigClasses.ActionContainer} style={{ justifyContent: 'center' }}>
                    <CustomTooltip content={'Edit Bidding Credentials'}>
                        <div onClick={() => setAddModal({ status: true, data: key })}>
                            <UilPen size={'1.2vw'} style={{ color: 'var(--color-primary)' }} />
                        </div>
                    </CustomTooltip>
                    <CustomTooltip content={'Delete Bidding Credentials'}>
                        <div onClick={() => setDeleteModal({ status: true, id: key._id })}>
                            <UilTrash size={'1.2vw'} style={{ color: 'var(--color-primary)' }} />
                        </div>
                    </CustomTooltip>
                </div>
            ),
        }));
        setTableData(processedData);
    };

    const handleSubmit = (values) => {
        const processedValues = {
            ...values,
            market: values.market.map((market) => market.value),
        };
        if (addModal.data?._id) {
            BidCredentialsService.Update(
                addModal.data?._id,
                processedValues,
                () => startLoader('updateBidCredentials'),
                () => handleCreateSuccess('updated'),
                handleError,
                () => stopLoader('updateBidCredentials')
            );
        } else {
            BidCredentialsService.Create(
                processedValues,
                () => startLoader('createdBidCredentials'),
                () => handleCreateSuccess('created'),
                handleError,
                () => stopLoader('createdBidCredentials')
            );
        }
    };

    const handleCreateSuccess = (type) => {
        fetchData();
        toast.success(`Bidding credentials ${type} sucessfully`);
        setAddModal({ status: false, data: {} });
    };

    const handleDelete = () => {
        if (deleteModal.id) {
            BidCredentialsService.Delete(
                deleteModal.id,
                startLoader,
                () => {
                    fetchData();
                    toast.success('Bidding Credentials deleted sucessfully');
                },
                handleError,
                stopLoader
            );
        }
    };

    const handleError = (err) => {
        let data = err && err.response ? err.response.data : null;
        if (data) toast.error(data.message);
        else toast.error('Internal server error!');
    };

    return (
        <div className={classes.AllDevices}>
            <ModalComponent
                isOpen={addModal.status}
                setOpen={(e) => {
                    setAddModal({ status: false, data: {} });
                }}
            >
                <div style={{ width: '35vw', minHeight: '45vh', maxHeight: '80vh', overflowY: 'scroll' }}>
                    <div className={GlobalConfigClasses.wholeFilterWrapper}>
                        <Typography content={addModal.data?._id ? 'Edit Bidding Credentials' : 'Add Bidding Credentials'} />
                    </div>
                    <div className={classes.SupportNoteForm}>
                        <Formik
                            initialValues={getInitialValues(addModal.data, selectedMarket, marketGroup, selectedMarketGroup)}
                            enableReinitialize
                            validationSchema={() => BidCredentialCreate(selectedMarket, marketGroup, selectedMarketGroup)}
                            onSubmit={(val) => {
                                handleSubmit(val);
                            }}
                        >
                            {({ errors, touched, values, isValidating, ...props }) => (
                                <Form>
                                    <div className={classes.InputContainer}>
                                        <div className={classes.FieldControl2}>
                                            <label for="marketGroup">
                                                Market Group <span className="required">*</span>
                                            </label>
                                            <DropdownComponent
                                                name="marketGroup"
                                                options={marketGroupOptions}
                                                defaultValue={selectedMarketGroup}
                                                onChange={(e) => {
                                                    setSelectedMarketGroup(e);
                                                    setMarketOptions(e.markets);
                                                    setSelectedMarket([e.markets[0]]);
                                                }}
                                                disabled={addModal.data?._id ? true : false}
                                            />
                                        </div>
                                        <div className={classes.FieldControl2}>
                                            <label for="market">
                                                Market <span className="required">*</span>
                                            </label>
                                            <MultiSelectDropdown
                                                name="market"
                                                options={marketOptions}
                                                defaultValue={addModal.data?.market?.length ? addModal.data?.market.map((item) => ({ label: item, value: item })) : selectedMarket}
                                                onChange={(e) => {
                                                    e.length > 0 && setSelectedMarket(e);
                                                }}
                                                disabled={addModal.data?._id ? true : false}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <div className={classes.InputContainer}>
                                            <div className={classes.FieldControl2}>
                                                <label for="name">
                                                    Name<span className="required">*</span>
                                                </label>
                                                <Input name="name" id="name" values={values} style={{ marginTop: '0' }} />
                                            </div>
                                            <div className={classes.FieldControl2}>
                                                <label for="email">
                                                    Email <span className="required">*</span>
                                                </label>
                                                <Input name="email" id="email" values={values} style={{ marginTop: '0' }} />
                                            </div>
                                        </div>
                                        {selectedMarket.some((market) => marketGroup.FCR.includes(market.value)) ? (
                                            <div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="BASE_URL">
                                                            Base URL <span className="required">*</span>
                                                        </label>
                                                        <Input name="BASE_URL" id="BASE_URL" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="USERNAME">
                                                            Username <span className="required">*</span>
                                                        </label>
                                                        <Input name="USERNAME" id="USERNAME" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="PASSWORD">
                                                            Password <span className="required">*</span>
                                                        </label>
                                                        <Input name="PASSWORD" id="PASSWORD" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (selectedMarket.some((market) => marketGroup.FCR_D.includes(market.value)) && selectedMarketGroup?.value === 'FCR_D') ||
                                            selectedMarket.some((market) => marketGroup.FFR.includes(market.value)) ? (
                                            <div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="BID_RESULT_BASE_URL">
                                                            Bid Result Base URL<span className="required">*</span>
                                                        </label>
                                                        <Input name="BID_RESULT_BASE_URL" id="BID_RESULT_BASE_URL" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="AUTH_TOKEN">
                                                            Authentication Token<span className="required">*</span>
                                                        </label>
                                                        <Input name="AUTH_TOKEN" id="AUTH_TOKEN" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : selectedMarket.some((market) => marketGroup.AFRR.includes(market.value)) ||
                                            selectedMarket.some((market) => marketGroup.MFRR.includes(market.value)) ? (
                                            <div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="BASE_URL">
                                                            Base URL <span className="required">*</span>
                                                        </label>
                                                        <Input name="BASE_URL" id="BASE_URL" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="API_KEY">
                                                            API Key <span className="required">*</span>
                                                        </label>
                                                        <Input name="API_KEY" id="API_KEY" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="BA_MESSAGE_ID">
                                                            BA Message ID <span className="required">*</span>
                                                        </label>
                                                        <Input name="BA_MESSAGE_ID" id="BA_MESSAGE_ID" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="SELF_EIC">
                                                            Self EIC<span className="required">*</span>
                                                        </label>
                                                        <Input name="SELF_EIC" id="SELF_EIC" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="ENERGY_EIC">
                                                            Energy EIC<span className="required">*</span>
                                                        </label>
                                                        <Input name="ENERGY_EIC" id="ENERGY_EIC" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="CAPACITY_EIC">
                                                            Capacity EIC<span className="required">*</span>
                                                        </label>
                                                        <Input name="CAPACITY_EIC" id="CAPACITY_EIC" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>{' '}
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="ENERGY_ADDRESS">
                                                            Energy Address<span className="required">*</span>
                                                        </label>
                                                        <Input name="ENERGY_ADDRESS" id="ENERGY_ADDRESS" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="CAPACITY_ADDRESS">
                                                            Capacity Address<span className="required">*</span>
                                                        </label>
                                                        <Input name="CAPACITY_ADDRESS" id="CAPACITY_ADDRESS" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="AVAILABILITY_PLANS_ADDRESS">
                                                            Availability Plans Address<span className="required">*</span>
                                                        </label>
                                                        <Input name="AVAILABILITY_PLANS_ADDRESS" id="AVAILABILITY_PLANS_ADDRESS" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="RESOURCE_SCHEDULE_ADDRESS">
                                                            Resource Schedule Address<span className="required">*</span>
                                                        </label>
                                                        <Input name="RESOURCE_SCHEDULE_ADDRESS" id="RESOURCE_SCHEDULE_ADDRESS" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : selectedMarket.some((market) => marketGroup.DC_PLANNER.includes(market.value)) ? (
                                            <div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="BASE_URL">
                                                            Base URL <span className="required">*</span>
                                                        </label>
                                                        <Input name="BASE_URL" id="BASE_URL" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="USERNAME">
                                                            Username <span className="required">*</span>
                                                        </label>
                                                        <Input name="USERNAME" id="USERNAME" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="PASSWORD">
                                                            Password <span className="required">*</span>
                                                        </label>
                                                        <Input name="PASSWORD" id="PASSWORD" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : selectedMarket.some((market) => marketGroup.FCR_D_FT.includes(market.value)) ? (
                                            <div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label htmlFor="FORTUM_BASE_URL">
                                                            Fortum Base URL <span className="required">*</span>
                                                        </label>
                                                        <Input name="FORTUM_BASE_URL" id="FORTUM_BASE_URL" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label htmlFor="KEY">
                                                            Key <span className="required">*</span>
                                                        </label>
                                                        <Input name="KEY" id="KEY" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                    <div className={classes.FieldControl2}>
                                                        <label htmlFor="SECRET">
                                                            Secret <span className="required">*</span>
                                                        </label>
                                                        <Input name="SECRET" id="SECRET" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : selectedMarket.some((market) => marketGroup.FCR_D_N_NMMS.includes(market.value)) ? (
                                            <div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="BASE_URL">
                                                            Base URL <span className="required">*</span>
                                                        </label>
                                                        <Input name="BASE_URL" id="BASE_URL" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="API_KEY">
                                                            API Key <span className="required">*</span>
                                                        </label>
                                                        <Input name="API_KEY" id="API_KEY" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="BA_MESSAGE_ID">
                                                            BA Message ID <span className="required">*</span>
                                                        </label>
                                                        <Input name="BA_MESSAGE_ID" id="BA_MESSAGE_ID" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="SELF_EIC">
                                                            Self EIC<span className="required">*</span>
                                                        </label>
                                                        <Input name="SELF_EIC" id="SELF_EIC" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="CAPACITY_EIC">
                                                            Capacity EIC<span className="required">*</span>
                                                        </label>
                                                        <Input name="CAPACITY_EIC" id="CAPACITY_EIC" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>{' '}
                                                <div className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2}>
                                                        <label for="CAPACITY_ADDRESS">
                                                            Capacity Address<span className="required">*</span>
                                                        </label>
                                                        <Input name="CAPACITY_ADDRESS" id="CAPACITY_ADDRESS" values={values} style={{ marginTop: '0' }} />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <></>
                                        )}
                                        <div className={classes.ButtonContainer}>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn-secondary"
                                                    onClick={(e) => {
                                                        setAddModal({ status: false, data: {} });
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                            <div>
                                                <button className="btn-primary" type="submit">
                                                    Submit
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </ModalComponent>
            <DeleteModal deletefunction={handleDelete} opendeleteModal={deleteModal.status} setOpenDeleteModal={(status) => setDeleteModal({ ...deleteModal, status })} />
            <div className={classes.Header}>
                <div>
                    <Typography content="Bidding Credentials" />
                    <div className={classes.TableCount}>
                        Total Count :<span>{tableData.length}</span>
                    </div>
                    <div>
                        <button type="submit" className="btn-primary" style={{ marginTop: '0' }} onClick={() => setAddModal({ status: true, data: {} })}>
                            Add
                        </button>
                    </div>
                </div>
            </div>
            <div>
                <Table head={['S.No', 'Name', 'Market', 'Action']} keys={['s.no', 'name', 'market', 'action']} data={tableData} />
            </div>
        </div>
    );
};

export default BidCredentials;
